import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { WorkboardTask } from 'api/models/WorkboardTask';
import { ApiService } from 'api/services/ApiService';
import { DashboardService } from 'api/services/DashboardService';
import { useZoom } from 'context/ZoomContext';
import { CALENDAR_DATE_FORMAT } from 'dashboard409a/common/constants/calendar';
import { WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY } from 'dashboard409a/hooks/useWorkboardTaskCalendarEvents';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import {
  AvatarRow,
  DateRow,
  DeleteModalActions,
  DeleteModalCloseButtonContainer,
  DeleteModalContainer,
  FooterBox,
  RoleBox,
  StatusRow,
  StyledBox,
  Wrapper,
} from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.styles';
import AnalystNotesHistoryPanel from 'dashboard409a/pages/Operations/components/NotesDisplay/AnalystNotesHistoryPanel';
import {
  Container,
  InfoRow,
  StyledLinkSpan,
} from 'dashboard409a/pages/Operations/components/ValuedEntity/ValuedEntity.styles';
import {
  GroupedUserAutocomplete,
  type GroupedUserAutocompleteDisplayItem,
} from 'dashboard409a/pages/Workboard/Filters/GroupedUserAutocomplete';
import type { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import { RootStateType } from 'dashboard409a/states/store';
import type { RecurrenceEndMode } from './CreateWorkboardTaskModal.constants';
import {
  CREATE_TASK_NOTES_HELPER_TEXT,
  groupedUserAutocompleteInputSx,
  RECURRENCE_ENDS_HELPER_TEXT,
  RECURRENCE_FREQUENCY_HELPER_TEXT,
  RECURRENCE_FREQUENCY_OPTIONS,
  RECURRENCE_INTERVAL_HELPER_TEXT,
  RECURRENCE_MONTHLY_FIRST_DAY_HELPER_TEXT,
  RECURRENCE_MONTHLY_FIRST_DAY_VALUE,
  RECURRENCE_MONTHLY_LAST_DAY_HELPER_TEXT,
  RECURRENCE_MONTHLY_LAST_DAY_VALUE,
  RECURRENCE_MONTHLY_SAME_DAY_VALUE,
  RECURRENCE_SETPOS_OPTIONS,
  RECURRENCE_SETPOS_ORDINAL_HELPER_TEXT,
  RECURRENCE_SETPOS_SAME_DAY_HELPER_TEXT,
  RECURRENCE_WEEKDAY_MONTHLY_HELPER_TEXT,
  RECURRENCE_WEEKDAY_OPTIONS,
  RECURRENCE_WEEKDAY_WEEKLY_HELPER_TEXT,
  TASK_STATUS_TRACKER_OPTIONS,
} from './CreateWorkboardTaskModal.constants';
import { CreateWorkboardTaskModalProps } from './CreateWorkboardTaskModal.props';
import {
  CloseButtonContainer,
  FieldLabel,
  FolderLocation,
  ModalFooter,
  StatusHeaderRow,
  TaskNameField,
  WorkboardTaskBentoGrid,
  workboardTaskModalBoxSx,
} from './CreateWorkboardTaskModal.styles';
import type { UserRoleField } from './CreateWorkboardTaskModal.types';
import {
  buildWorkboardTaskOccurrencePatchPayload,
  buildWorkboardTaskPayload,
  type CreateWorkboardTaskFormState,
  getCustomUserAvatarData,
  getCustomUserDisplayName,
  getCustomUsersAvatarData,
  getCustomUsersDisplayName,
  getDefaultFormState,
  getWeekdayCodeFromDate,
  isRecurringFrequency,
  mapWorkboardTaskToFormState,
  validateCreateWorkboardTaskForm,
} from './CreateWorkboardTaskModal.utilities';
import StatusCheckboxOption from './StatusCheckboxOption';

const CreateWorkboardTaskModal = ({
  open,
  onClose,
  defaultStartsOn,
  taskId = null,
  occurrenceDate = null,
}: CreateWorkboardTaskModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { zoomLevel } = useZoom();
  const currentUser = useSelector((state: RootStateType) => state.user);
  const isEditMode = taskId != null;
  const [form, setForm] = useState<CreateWorkboardTaskFormState>(() => getDefaultFormState(defaultStartsOn));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assigneeInput, setAssigneeInput] = useState('');
  const [taskManagerInput, setTaskManagerInput] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isUnassignedConfirmOpen, setIsUnassignedConfirmOpen] = useState(false);

  const { data: staffUsers = [], isLoading: isLoadingStaff } = useQuery<DashboardSmallUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return response.filter(user => !user.email?.includes('+')) || [];
    },
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const { data: existingTask, isLoading: isLoadingTask } = useQuery({
    queryKey: ['workboard-task', taskId],
    queryFn: () => DashboardService.apiDashboardWorkboardTasksRead(String(taskId)),
    enabled: open && isEditMode && taskId != null,
  });

  const isRecurringTask = isRecurringFrequency(existingTask?.recurrence_frequency);

  const { data: existingOccurrence, isLoading: isLoadingOccurrence } = useQuery({
    queryKey: ['workboard-task-occurrence', taskId, occurrenceDate],
    queryFn: () => DashboardService.apiDashboardWorkboardTasksOccurrencesRead(String(taskId), occurrenceDate!),
    enabled: open && isEditMode && taskId != null && occurrenceDate != null && isRecurringTask,
  });

  useEffect(() => {
    if (!open) {
      setAssigneeInput('');
      setTaskManagerInput('');
      setIsDeleteConfirmOpen(false);
      setIsUnassignedConfirmOpen(false);
      return;
    }

    setSubmitError(null);

    if (isEditMode) {
      if (!existingTask) {
        return;
      }

      if (isRecurringTask && occurrenceDate != null && isLoadingOccurrence) {
        return;
      }

      setForm(
        mapWorkboardTaskToFormState(
          existingTask,
          staffUsers,
          occurrenceDate,
          existingOccurrence?.status,
          existingOccurrence?.rescheduled_to,
          existingOccurrence?.is_rush
        )
      );
      return;
    }

    setForm(getDefaultFormState(defaultStartsOn));
  }, [
    open,
    isEditMode,
    defaultStartsOn,
    existingTask,
    existingOccurrence,
    isLoadingOccurrence,
    isRecurringTask,
    staffUsers,
    taskId,
    occurrenceDate,
  ]);

  const isRecurring = isRecurringFrequency(form.recurrenceFrequency);
  const showRushCheckbox = !isRecurring || (isEditMode && isRecurringTask && occurrenceDate != null);
  const showWeekdayPicker =
    form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY ||
    (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY &&
      form.recurrenceBySetpos !== null &&
      form.recurrenceByMonthday === null);
  const showSetposPicker = form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY;
  const fieldsDisabled = form.isWaiting || isSaving || isDeleting;
  const isFormLoading =
    isEditMode && (isLoadingTask || (isRecurringTask && occurrenceDate != null && isLoadingOccurrence));
  const hasFolder = Boolean(form.fileReferenceFolder.trim());
  // Notes attach to the scheduled occurrence identity, not a rescheduled display date.
  const notesOccurrenceDate = useMemo(() => {
    if (occurrenceDate) {
      return occurrenceDate;
    }

    if (form.occurrenceDate) {
      return moment(form.occurrenceDate).format(CALENDAR_DATE_FORMAT);
    }

    return null;
  }, [form.occurrenceDate, occurrenceDate]);
  const workboardTaskOccurrence = useMemo(
    () =>
      isEditMode && taskId != null && notesOccurrenceDate
        ? { workboardTaskId: taskId, occurrenceDate: notesOccurrenceDate }
        : undefined,
    [isEditMode, taskId, notesOccurrenceDate]
  );
  const areNotesDisabled = !workboardTaskOccurrence || isSaving || isDeleting;

  const updateForm = <K extends keyof CreateWorkboardTaskFormState>(key: K, value: CreateWorkboardTaskFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSubmitError(null);
  };

  const handleRecurrenceFrequencyChange = (frequency: WorkboardTask.recurrence_frequency) => {
    setForm(prev => {
      const next: CreateWorkboardTaskFormState = {
        ...prev,
        recurrenceFrequency: frequency,
        recurrenceEndMode: frequency === WorkboardTask.recurrence_frequency.NONE ? 'none' : prev.recurrenceEndMode,
      };

      if (frequency === WorkboardTask.recurrence_frequency.WEEKLY) {
        next.recurrenceBySetpos = null;
        next.recurrenceByMonthday = null;
        if (next.recurrenceByWeekday.length === 0 && next.occurrenceDate) {
          next.recurrenceByWeekday = [getWeekdayCodeFromDate(next.occurrenceDate)];
        }
      } else if (frequency === WorkboardTask.recurrence_frequency.MONTHLY) {
        // Calendar-day patterns must not carry leftover weekly/ordinal weekdays.
        if (next.recurrenceByMonthday !== null || next.recurrenceBySetpos === null) {
          next.recurrenceByWeekday = [];
        }
      } else {
        next.recurrenceByWeekday = [];
        next.recurrenceBySetpos = null;
        next.recurrenceByMonthday = null;
      }

      return next;
    });
    setSubmitError(null);
  };

  const handleRecurrenceBySetposChange = (value: number | null) => {
    setForm(prev => {
      const next: CreateWorkboardTaskFormState = {
        ...prev,
        recurrenceBySetpos: value,
        recurrenceByMonthday: null,
      };

      if (value === null) {
        next.recurrenceByWeekday = [];
      } else if (next.recurrenceByWeekday.length === 0 && next.occurrenceDate) {
        next.recurrenceByWeekday = [getWeekdayCodeFromDate(next.occurrenceDate)];
      }

      return next;
    });
    setSubmitError(null);
  };

  const handleMonthlyPatternChange = (raw: string | number) => {
    if (raw === RECURRENCE_MONTHLY_SAME_DAY_VALUE) {
      setForm(prev => ({
        ...prev,
        recurrenceBySetpos: null,
        recurrenceByMonthday: null,
        recurrenceByWeekday: [],
      }));
      setSubmitError(null);
      return;
    }

    if (raw === RECURRENCE_MONTHLY_FIRST_DAY_VALUE) {
      setForm(prev => ({
        ...prev,
        recurrenceBySetpos: null,
        recurrenceByMonthday: 1,
        recurrenceByWeekday: [],
      }));
      setSubmitError(null);
      return;
    }

    if (raw === RECURRENCE_MONTHLY_LAST_DAY_VALUE) {
      setForm(prev => ({
        ...prev,
        recurrenceBySetpos: null,
        recurrenceByMonthday: -1,
        recurrenceByWeekday: [],
      }));
      setSubmitError(null);
      return;
    }

    handleRecurrenceBySetposChange(Number(raw));
  };

  const monthlyPatternSelectValue = (() => {
    if (form.recurrenceByMonthday === 1) {
      return RECURRENCE_MONTHLY_FIRST_DAY_VALUE;
    }
    if (form.recurrenceByMonthday === -1) {
      return RECURRENCE_MONTHLY_LAST_DAY_VALUE;
    }
    if (form.recurrenceBySetpos !== null) {
      return form.recurrenceBySetpos;
    }
    return RECURRENCE_MONTHLY_SAME_DAY_VALUE;
  })();

  const monthlyPatternHelperText = (() => {
    if (form.recurrenceByMonthday === 1) {
      return RECURRENCE_MONTHLY_FIRST_DAY_HELPER_TEXT;
    }
    if (form.recurrenceByMonthday === -1) {
      return RECURRENCE_MONTHLY_LAST_DAY_HELPER_TEXT;
    }
    if (form.recurrenceBySetpos !== null) {
      return RECURRENCE_SETPOS_ORDINAL_HELPER_TEXT;
    }
    return RECURRENCE_SETPOS_SAME_DAY_HELPER_TEXT;
  })();

  const handleOccurrenceDateChange = (date: Date | null) => {
    setForm(prev => {
      const next: CreateWorkboardTaskFormState = {
        ...prev,
        occurrenceDate: date,
        startsOn:
          !isEditMode || prev.recurrenceFrequency === WorkboardTask.recurrence_frequency.NONE ? date : prev.startsOn,
      };

      if (
        date &&
        prev.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY &&
        prev.recurrenceByWeekday.length === 0
      ) {
        next.recurrenceByWeekday = [getWeekdayCodeFromDate(date)];
      }

      return next;
    });
    setSubmitError(null);
  };

  const currentUserAsAssignee = useMemo((): SelectedUserWithGroup | null => {
    if (!currentUser?.id) {
      return null;
    }

    return {
      id: currentUser.id,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
      email: currentUser.email,
      picture_url: currentUser.profile?.picture_url ?? null,
      is_staff: currentUser.is_staff,
    };
  }, [currentUser]);

  const handleClose = () => {
    if (isSaving || isDeleting) {
      return;
    }
    onClose();
  };

  const handleSave = async (formToSave: CreateWorkboardTaskFormState = form) => {
    const validationError = validateCreateWorkboardTaskForm(formToSave);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    try {
      if (isEditMode && taskId != null) {
        const isOccurrenceEdit =
          isRecurringFrequency(formToSave.recurrenceFrequency) && formToSave.occurrenceDate != null;

        if (isOccurrenceEdit) {
          if (!occurrenceDate) {
            throw new Error('Missing scheduled occurrence date for recurring task edit.');
          }
          await DashboardService.apiDashboardWorkboardTasksOccurrencesPartialUpdate(
            String(taskId),
            buildWorkboardTaskOccurrencePatchPayload(formToSave, occurrenceDate)
          );
          await DashboardService.apiDashboardWorkboardTasksPartialUpdate(
            String(taskId),
            buildWorkboardTaskPayload(formToSave, { forUpdate: true })
          );
        } else {
          await DashboardService.apiDashboardWorkboardTasksUpdate(
            String(taskId),
            buildWorkboardTaskPayload(formToSave, { forUpdate: true })
          );
        }
        await queryClient.invalidateQueries({ queryKey: ['workboard-task', taskId] });
        await queryClient.invalidateQueries({ queryKey: ['workboard-task-occurrence', taskId] });
        enqueueSnackbar('Task updated', { variant: 'success' });
      } else {
        await DashboardService.apiDashboardWorkboardTasksCreate(buildWorkboardTaskPayload(formToSave));
        enqueueSnackbar('Task created', { variant: 'success' });
      }
      await queryClient.invalidateQueries({ queryKey: [WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY] });
      onClose();
    } catch {
      enqueueSnackbar(
        isEditMode ? 'Failed to update task. Please try again.' : 'Failed to create task. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitClick = () => {
    const validationError = validateCreateWorkboardTaskForm(form);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    if (!isEditMode && form.assignedTo.length === 0) {
      setIsUnassignedConfirmOpen(true);
      return;
    }

    handleSave();
  };

  const handleCloseUnassignedConfirm = () => {
    if (!isSaving) {
      setIsUnassignedConfirmOpen(false);
    }
  };

  const handleCreateUnassigned = () => {
    setIsUnassignedConfirmOpen(false);
    handleSave();
  };

  const handleAssignMyselfAndSave = () => {
    if (!currentUserAsAssignee) {
      enqueueSnackbar('You cannot assign yourself. Please select someone else.', { variant: 'error' });
      return;
    }

    const nextForm = { ...form, assignedTo: [currentUserAsAssignee] };
    setForm(nextForm);
    setIsUnassignedConfirmOpen(false);
    handleSave(nextForm);
  };

  const handleDeleteClick = () => {
    if (!isEditMode || taskId == null) {
      return;
    }

    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!isEditMode || taskId == null) {
      return;
    }

    setIsDeleting(true);
    try {
      await DashboardService.apiDashboardWorkboardTasksDelete(String(taskId));
      await queryClient.invalidateQueries({ queryKey: [WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY] });
      enqueueSnackbar('Task deleted', { variant: 'success' });
      setIsDeleteConfirmOpen(false);
      onClose();
    } catch {
      enqueueSnackbar('Failed to delete task. Please try again.', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDialogTags = useCallback((displayItems: GroupedUserAutocompleteDisplayItem[], maxVisible: number) => {
    if (displayItems.length === 0) {
      return null;
    }

    const visible = displayItems.slice(0, maxVisible);
    const remaining = displayItems.length - maxVisible;
    const chipSx: SxProps<Theme> = {
      height: 20,
      fontSize: '0.7rem',
      bgcolor: 'action.hover',
      '& .MuiChip-label': { px: 0.5 },
      '& .MuiChip-icon': { fontSize: '0.75rem', ml: '4px' },
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden', mr: 0.5 }}>
        {visible.map(item => (
          <Chip
            key={item.data.id}
            size="small"
            icon={<AccountCircleIcon />}
            label={
              item.kind === 'group'
                ? item.data.name
                : [item.data.first_name, item.data.last_name].filter(Boolean).join(' ') || item.data.email || ''
            }
            sx={chipSx}
          />
        ))}
        {remaining > 0 && <Chip size="small" icon={<AccountCircleIcon />} label={`+${remaining}`} sx={chipSx} />}
      </Box>
    );
  }, []);

  const renderUserRoleBox = (
    field: UserRoleField,
    label: string,
    placeholder: string,
    inputValue: string,
    onInputChange: (value: string) => void,
    testId: string
  ) => {
    const users = form[field];

    return (
      <RoleBox>
        <Wrapper>
          <Typography fontWeight={600} fontSize="0.8rem">
            {label}
          </Typography>
        </Wrapper>
        {fieldsDisabled ? (
          <AvatarRow>
            {users.length > 0 ? (
              users.map(user => (
                <Avatar
                  key={user.id}
                  data={getCustomUserAvatarData(user)}
                  name={getCustomUserDisplayName(user)}
                  isLoading={isLoadingStaff}
                />
              ))
            ) : (
              <Avatar
                data={getCustomUsersAvatarData(users)}
                name={getCustomUsersDisplayName(users)}
                isLoading={isLoadingStaff}
              />
            )}
          </AvatarRow>
        ) : (
          <Box sx={{ mt: 0.5, width: '100%', minWidth: 160 }} data-testid={testId}>
            <GroupedUserAutocomplete
              value={users}
              onChange={selectedUsers => updateForm(field, selectedUsers)}
              inputValue={inputValue}
              onInputChange={onInputChange}
              label=""
              placeholder={placeholder}
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={groupedUserAutocompleteInputSx}
              disabled={isSaving || isDeleting}
              autocompleteRootSx={{ width: '100%' }}
            />
          </Box>
        )}
      </RoleBox>
    );
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={workboardTaskModalBoxSx} onClick={e => e.stopPropagation()}>
          <CloseButtonContainer>
            <IconButton onClick={handleClose} size="small" aria-label="close" sx={{ padding: '4px' }}>
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </CloseButtonContainer>

          {isFormLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <WorkboardTaskBentoGrid showNotesColumn={Boolean(workboardTaskOccurrence)}>
                <Section title="Task Details">
                  <Container>
                    <TaskNameField>
                      <SettingsIcon color="primary" fontSize="small" />
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Task name"
                        value={form.name}
                        onChange={e => updateForm('name', e.target.value)}
                        autoFocus
                        variant="standard"
                        slotProps={{
                          input: {
                            sx: theme => ({
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                            }),
                          }}}
                        data-testid="create-workboard-task-name"
                      />
                    </TaskNameField>

                    <InfoRow>
                      <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <FieldLabel>Documentation Folder</FieldLabel>
                          {hasFolder && <Chip label="Folder set" color="success" size="small" variant="outlined" />}
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Folder path"
                          value={form.fileReferenceFolder}
                          onChange={e => updateForm('fileReferenceFolder', e.target.value)}
                          sx={{ mt: 0.5 }}
                          data-testid="create-workboard-task-folder"
                        />
                        {hasFolder && <FolderLocation>Location: {form.fileReferenceFolder.trim()}</FolderLocation>}
                      </Box>

                      <Box>
                        <FieldLabel>Related Documents</FieldLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Link to related documents"
                          value={form.linkToRelatedDocuments}
                          onChange={e => updateForm('linkToRelatedDocuments', e.target.value)}
                          sx={{ mt: 0.5 }}
                          data-testid="create-workboard-task-link"
                        />
                        {form.linkToRelatedDocuments.trim() && (
                          <Box sx={{ mt: 0.5 }}>
                            <Link href={form.linkToRelatedDocuments.trim()} target="_blank" rel="noopener noreferrer">
                              <StyledLinkSpan>Open link</StyledLinkSpan>
                            </Link>
                          </Box>
                        )}
                      </Box>
                    </InfoRow>
                  </Container>
                </Section>

                <StyledBox>
                  <Typography variant="h3" fontWeight={600} color="text.primary">
                    Task Status
                  </Typography>
                  <StatusHeaderRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <StatusCheckboxOption
                        tooltip="When enabled, status and due date fields are disabled."
                        checked={form.isWaiting}
                        onChange={checked => updateForm('isWaiting', checked)}
                        disabled={isSaving || isDeleting}
                        testId="create-workboard-task-is-waiting"
                        label="Is Waiting"
                        icon={<PauseCircleIcon fontSize="small" color="primary" />}
                      />
                      {showRushCheckbox && (
                        <StatusCheckboxOption
                          tooltip="Flag this occurrence as a rush item."
                          checked={form.isRush}
                          onChange={checked => updateForm('isRush', checked)}
                          disabled={isSaving || isDeleting}
                          testId="create-workboard-task-is-rush"
                          label="Rush"
                          icon={<SpeedIcon fontSize="small" color="primary" />}
                        />
                      )}
                    </Box>
                    {isEditMode && (
                      <Tooltip title="Delete task">
                        <IconButton size="small" onClick={handleDeleteClick} disabled={isSaving || isDeleting}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </StatusHeaderRow>

                  <Box flex={1} display="flex" flexDirection="column" width="100%">
                    <DateRow sx={{ mt: 1 }}>
                      <Typography fontWeight={600} fontSize="0.85rem">
                        Task Due Date
                      </Typography>
                      <ConditionalLoader isLoading={false} width={150}>
                        <MobileDatePicker
                          value={form.occurrenceDate}
                          onChange={handleOccurrenceDateChange}
                          format="MM/dd/yyyy"
                          disabled={fieldsDisabled}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { maxWidth: 160, '& .MuiInputBase-input': { padding: '4px 8px' } },
                              inputProps: { 'data-testid': 'create-workboard-task-occurrence-date' },
                            },
                          }}
                        />
                      </ConditionalLoader>
                      <CalendarMonthIcon fontSize="small" color={fieldsDisabled ? 'disabled' : 'primary'} />
                    </DateRow>

                    <StatusRow sx={{ opacity: form.isWaiting ? 0.5 : 1, mt: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <ValuationStatusTracker
                          statusOptions={TASK_STATUS_TRACKER_OPTIONS}
                          currentStatusId={form.status}
                          onStatusChange={statusId => updateForm('status', statusId as WorkboardTask.status)}
                          disabled={fieldsDisabled}
                          fixedHeight="auto"
                          isFromModels
                        />
                      </Box>

                      <FooterBox>
                        {renderUserRoleBox(
                          'assignedTo',
                          'Assigned To',
                          'Select assignees',
                          assigneeInput,
                          setAssigneeInput,
                          'create-workboard-task-assigned-to'
                        )}
                        {renderUserRoleBox(
                          'assignedTaskManager',
                          'Manager',
                          'Select task managers',
                          taskManagerInput,
                          setTaskManagerInput,
                          'create-workboard-task-status-manager'
                        )}
                      </FooterBox>
                    </StatusRow>
                  </Box>
                </StyledBox>

                <Section title="Recurrence">
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="create-workboard-task-recurrence-frequency-label">Frequency</InputLabel>
                      <Select
                        labelId="create-workboard-task-recurrence-frequency-label"
                        label="Frequency"
                        value={form.recurrenceFrequency}
                        onChange={e =>
                          handleRecurrenceFrequencyChange(e.target.value as WorkboardTask.recurrence_frequency)
                        }
                        disabled={isSaving || isDeleting}
                        data-testid="create-workboard-task-recurrence-frequency">
                        {RECURRENCE_FREQUENCY_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{RECURRENCE_FREQUENCY_HELPER_TEXT[form.recurrenceFrequency]}</FormHelperText>
                    </FormControl>

                    {isRecurring && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Repeat every"
                        value={form.recurrenceInterval}
                        onChange={e => updateForm('recurrenceInterval', Number(e.target.value) || 1)}
                        disabled={isSaving || isDeleting}
                        slotProps={{
                          input: {
                            inputProps: { min: 1, 'data-testid': 'create-workboard-task-recurrence-interval' },
                          },
                        }}
                        helperText={RECURRENCE_INTERVAL_HELPER_TEXT[form.recurrenceFrequency] ?? ''}
                      />
                    )}

                    {showSetposPicker && (
                      <FormControl fullWidth size="small">
                        <InputLabel id="create-workboard-task-setpos-label" shrink>
                          Monthly pattern
                        </InputLabel>
                        <Select
                          labelId="create-workboard-task-setpos-label"
                          label="Monthly pattern"
                          notched
                          value={monthlyPatternSelectValue}
                          onChange={e => handleMonthlyPatternChange(e.target.value)}
                          disabled={isSaving || isDeleting}
                          data-testid="create-workboard-task-recurrence-setpos">
                          <MenuItem value={RECURRENCE_MONTHLY_SAME_DAY_VALUE}>Same day of month</MenuItem>
                          <MenuItem value={RECURRENCE_MONTHLY_FIRST_DAY_VALUE}>First day of month</MenuItem>
                          <MenuItem value={RECURRENCE_MONTHLY_LAST_DAY_VALUE}>Last day of month</MenuItem>
                          {RECURRENCE_SETPOS_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{monthlyPatternHelperText}</FormHelperText>
                      </FormControl>
                    )}

                    {showWeekdayPicker && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Weekdays
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive={false}
                          value={form.recurrenceByWeekday}
                          onChange={(_, weekdays) => updateForm('recurrenceByWeekday', weekdays)}
                          disabled={isSaving || isDeleting}
                          aria-label="Recurrence weekdays">
                          {RECURRENCE_WEEKDAY_OPTIONS.map(option => (
                            <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                              {option.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        <FormHelperText>
                          {form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY
                            ? RECURRENCE_WEEKDAY_WEEKLY_HELPER_TEXT
                            : RECURRENCE_WEEKDAY_MONTHLY_HELPER_TEXT}
                        </FormHelperText>
                      </Box>
                    )}

                    {isRecurring && (
                      <FormControl>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          Ends
                        </Typography>
                        <RadioGroup
                          value={form.recurrenceEndMode}
                          onChange={e => updateForm('recurrenceEndMode', e.target.value as RecurrenceEndMode)}>
                          <FormControlLabel
                            value="none"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="Never"
                          />
                          <FormControlLabel
                            value="date"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="On date"
                          />
                          <FormControlLabel
                            value="count"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="After occurrences"
                          />
                        </RadioGroup>
                        <FormHelperText>{RECURRENCE_ENDS_HELPER_TEXT[form.recurrenceEndMode]}</FormHelperText>
                        {form.recurrenceEndMode === 'date' && (
                          <MobileDatePicker
                            label="Recurrence end date"
                            value={form.recurrenceEndsOn}
                            onChange={date => updateForm('recurrenceEndsOn', date)}
                            minDate={form.occurrenceDate ?? undefined}
                            disabled={isSaving || isDeleting}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { mt: 1, maxWidth: 280 },
                                inputProps: { 'data-testid': 'create-workboard-task-recurrence-ends-on' },
                              },
                            }}
                          />
                        )}
                        {form.recurrenceEndMode === 'count' && (
                          <TextField
                            size="small"
                            type="number"
                            label="Occurrence count"
                            value={form.recurrenceCount ?? ''}
                            onChange={e => updateForm('recurrenceCount', Number(e.target.value) || null)}
                            disabled={isSaving || isDeleting}
                            slotProps={{
                              input: {
                                inputProps: { min: 1, 'data-testid': 'create-workboard-task-recurrence-count' },
                              },
                            }}
                            sx={{ mt: 1, maxWidth: 280 }}
                          />
                        )}
                      </FormControl>
                    )}
                  </Stack>
                </Section>
                <Section title="Task Notes">
                  {workboardTaskOccurrence ? (
                    <AnalystNotesHistoryPanel
                      referenceType="wo"
                      workboardTaskOccurrence={workboardTaskOccurrence}
                      isDisabled={areNotesDisabled}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {CREATE_TASK_NOTES_HELPER_TEXT}
                    </Typography>
                  )}
                </Section>
              </WorkboardTaskBentoGrid>

              {submitError && (
                <Typography variant="body2" color="error" sx={{ px: 1, pt: 1 }}>
                  {submitError}
                </Typography>
              )}

              <ModalFooter>
                <Button onClick={handleClose} disabled={isSaving || isDeleting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitClick}
                  variant="contained"
                  disabled={isSaving || isDeleting}
                  data-testid="create-workboard-task-submit">
                  {isEditMode ? 'Save Task' : 'Create Task'}
                </Button>
              </ModalFooter>
            </>
          )}
        </Box>
      </Modal>
      <Modal
        open={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteConfirmOpen(false);
          }
        }}>
        <DeleteModalContainer>
          {isDeleting && <LinearProgress color="error" />}
          <DeleteModalCloseButtonContainer>
            <IconButton
              size="small"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeleting}
              aria-label="Close delete task dialog">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DeleteModalCloseButtonContainer>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Delete Task
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <DeleteIcon fontSize="small" color="error" />
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this task? This action cannot be undone.
            </Typography>
          </Box>
          <DeleteModalActions>
            <Button variant="outlined" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={isDeleting}>
              Delete
            </Button>
          </DeleteModalActions>
        </DeleteModalContainer>
      </Modal>
      <Modal open={isUnassignedConfirmOpen} onClose={handleCloseUnassignedConfirm}>
        <DeleteModalContainer data-testid="create-workboard-task-unassigned-confirm">
          {isSaving && <LinearProgress />}
          <DeleteModalCloseButtonContainer>
            <IconButton
              size="small"
              onClick={handleCloseUnassignedConfirm}
              disabled={isSaving}
              aria-label="Close unassigned task dialog">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DeleteModalCloseButtonContainer>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            No Assignees
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <PersonAddIcon fontSize="small" color="primary" />
            <Typography variant="body1" color="text.secondary">
              This task has no assignees. Assign yourself, or create it unassigned?
            </Typography>
          </Box>
          <DeleteModalActions>
            <Button
              variant="outlined"
              onClick={handleCloseUnassignedConfirm}
              disabled={isSaving}
              data-testid="create-workboard-task-unassigned-cancel">
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleCreateUnassigned}
              disabled={isSaving}
              data-testid="create-workboard-task-unassigned-create">
              Create Unassigned
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignMyselfAndSave}
              disabled={isSaving || !currentUserAsAssignee}
              data-testid="create-workboard-task-unassigned-assign-self">
              Assign Myself
            </Button>
          </DeleteModalActions>
        </DeleteModalContainer>
      </Modal>
    </>
  );
};

export default CreateWorkboardTaskModal;
